import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Twitter, Github, MessageCircle } from 'lucide-react';
import Logo from './Logo';
import { APP_DASHBOARD_URL } from '../lib/appUrl';
import { COMPARE_ENABLED } from '../lib/features';

const FEATURES_LINKS = [
  { label: 'AI Prioritization', href: '/features/ai-prioritization', testId: 'footer-feature-ai' },
  { label: 'Roadmap', href: '/features/roadmap', testId: 'footer-feature-roadmap' },
  { label: 'Changelog', href: '/features/changelog', testId: 'footer-feature-changelog' },
];

const PRODUCT_LINKS = [
  { label: 'Dashboard', href: APP_DASHBOARD_URL, testId: 'footer-product-dashboard' },
  { label: 'Roadmap view', href: '/roadmap', testId: 'footer-product-roadmap' },
  { label: 'Changelog view', href: '/changelog', testId: 'footer-product-changelog' },
  { label: 'Weekly digest', href: '/digest', testId: 'footer-product-digest' },
  { label: 'Pricing', href: '/pricing', testId: 'footer-pricing-link' },
];

const COMPARE_LINKS = [
  { label: 'vs Canny', href: '/compare/canny', testId: 'footer-compare-canny' },
  { label: 'vs UserJot', href: '/compare/userjot', testId: 'footer-compare-userjot' },
  { label: 'vs Featurebase', href: '/compare/featurebase', testId: 'footer-compare-featurebase' },
  { label: 'vs Frill', href: '/compare/frill', testId: 'footer-compare-frill' },
];

const RESOURCES_LINKS = [
  { label: 'FAQ', href: '/faq', testId: 'footer-faq-link' },
  { label: 'Documentation', href: '/faq', testId: 'footer-docs-link', note: 'soon' },
  { label: 'Founder\u2019s playbook', href: '/faq', testId: 'footer-playbook-link', note: 'soon' },
  { label: 'Public roadmap', href: '/roadmap', testId: 'footer-public-roadmap-link' },
  { label: 'API reference', href: '/faq', testId: 'footer-api-link', note: 'soon' },
];

const HELP_LINKS = [
  { label: 'Contact support', href: 'mailto:support@vibesprint.ai', testId: 'footer-contact-link' },
  { label: 'Community', href: '/faq', testId: 'footer-community-link', note: 'soon' },
  { label: 'System status', href: '/faq', testId: 'footer-status-link', note: 'soon' },
  { label: 'Report a bug', href: 'mailto:support@vibesprint.ai?subject=Bug%20report', testId: 'footer-bug-link' },
];

const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: '/privacy', testId: 'footer-privacy-link' },
  { label: 'Terms of Service', href: '/terms', testId: 'footer-terms-link' },
  { label: 'Cookie Policy', href: '/cookies', testId: 'footer-cookies-link' },
  { label: 'Refund Policy', href: '/refund', testId: 'footer-refund-link' },
];

const SOCIAL_LINKS = [
  { label: 'Twitter / X', href: 'https://twitter.com', testId: 'footer-social-twitter', Icon: Twitter },
  { label: 'GitHub', href: 'https://github.com', testId: 'footer-social-github', Icon: Github },
  { label: 'Community chat', href: 'mailto:support@vibesprint.ai', testId: 'footer-social-community', Icon: MessageCircle },
];

const LinkColumn = ({ title, links, testId }) => (
  <div data-testid={testId}>
    <h4 className="text-sm font-semibold mb-3 text-foreground">{title}</h4>
    <ul className="space-y-2">
      {links.map((l) => (
        <li key={l.label}>
          <a
            href={l.href}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1.5"
            data-testid={l.testId}
          >
            {l.label}
            {l.note && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground border">
                {l.note}
              </span>
            )}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = ({ variant = 'default', compareEnabled = COMPARE_ENABLED }) => {
  if (variant === 'minimal') {
    return (
      <footer className="border-t py-6 bg-background mt-12" data-testid="footer-minimal">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
            <p className="text-muted-foreground">
              © 2026 VibeSprint. Built for founders who ship.
            </p>
            <div className="flex items-center gap-5 flex-wrap justify-center">
              <a href="/pricing" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-pricing-link">Pricing</a>
              <a href="/faq" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-faq-link">FAQ</a>
              {compareEnabled && (
                <a href="/compare/canny" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-compare-canny">Compare</a>
              )}
              <a href="/privacy" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy-link">Privacy</a>
              <a href="/terms" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-terms-link">Terms</a>
              <a href="/cookies" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-cookies-link">Cookies</a>
              <a href="/refund" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-refund-link">Refunds</a>
              <a href="mailto:support@vibesprint.ai" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-contact-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t bg-muted/20" data-testid="footer-full">
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-10">
          <LinkColumn title="Features" links={FEATURES_LINKS} testId="footer-column-features" />
          <LinkColumn title="Product" links={PRODUCT_LINKS} testId="footer-column-product" />
          {compareEnabled ? (
            <LinkColumn title="Compare" links={COMPARE_LINKS} testId="footer-column-compare" />
          ) : (
            <LinkColumn title="Compare (coming soon)" links={[]} testId="footer-column-compare" />
          )}
          <LinkColumn title="Resources" links={RESOURCES_LINKS} testId="footer-column-resources" />
          <LinkColumn title="Get Help" links={HELP_LINKS} testId="footer-column-help" />
          <LinkColumn title="Legal" links={LEGAL_LINKS} testId="footer-column-legal" />
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-8 border-t">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Logo size="sm" />
              <span className="text-xs px-1.5 py-0.5 rounded-md border border-orange-500/50 text-orange-600 uppercase tracking-wider font-semibold">
                Alpha
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 VibeSprint. Built for founders who ship.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {SOCIAL_LINKS.map(({ label, href, testId, Icon }) => (
              <a
                key={label}
                href={href}
                target={href.startsWith('http') ? '_blank' : undefined}
                rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                aria-label={label}
                title={label}
                className="h-9 w-9 rounded-md border bg-card flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                data-testid={testId}
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
            <Button
              asChild
              className="btn-cta ml-3"
              data-testid="footer-cta-button"
            >
              <a href={APP_DASHBOARD_URL}>
                Go to Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
